import { useMemo, useState } from 'react';

import { useIsFlexSwitchToWhatsappCity } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-is-flex-switch-to-whatsapp-city.hook';
import { SchedulesSearchProcedure, ScheduleType, SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';

export enum TabsType {
  SCHEDULED = 'SCHEDULED',
  ARRIVAL_ORDER = 'ARRIVAL_ORDER',
  FLEX = 'FLEX',
  WHATSAPP = 'WHATSAPP',
  STANDALONE = 'STANDALONE',
  LABORATORIAL = 'LABORATORIAL',
}

export type TabItem = { title: string; key: TabsType };

export const TABS_LABELS: Record<TabsType, TabItem> = {
  [TabsType.SCHEDULED]: { title: 'Hora marcada', key: TabsType.SCHEDULED },
  [TabsType.ARRIVAL_ORDER]: { title: 'Ordem de chegada', key: TabsType.ARRIVAL_ORDER },
  [TabsType.FLEX]: { title: 'Flex', key: TabsType.FLEX },
  [TabsType.WHATSAPP]: { title: 'WhatsApp', key: TabsType.WHATSAPP },
  [TabsType.STANDALONE]: { title: 'Guia avulsa', key: TabsType.STANDALONE },
  [TabsType.LABORATORIAL]: { title: 'Ordem de chegada', key: TabsType.LABORATORIAL },
} as const;

const STANDALONE_BLACKLIST = new Set([
  '6dd09371-9178-4151-8a34-a2bf34b0e000',
  'c38bbcf8-10da-46bc-b8a3-bc0ee9915aa7',
  '7e6c7c24-9862-4777-abcd-227ca78d4ab6',
]);

const hasType = (schedule?: SearchPartnerData, type?: ScheduleType) =>
  Boolean(type && schedule?.schedulesTypes?.[type]?.length);

const isTelehealthProcedure = (procedures?: SchedulesSearchProcedure[]) =>
  procedures?.some((procedure) => procedure.availableForTelehealth) ?? false;

const hasStandalone = (schedule?: SearchPartnerData) =>
  schedule?.partner.id && !isTelehealthProcedure(schedule.procedures) && !STANDALONE_BLACKLIST.has(schedule.partner.id);

export function useScheduleTab(schedule?: SearchPartnerData) {
  const [selectedTab, setSelectedTab] = useState<TabsType | undefined>();
  const isWhatsappCity = useIsFlexSwitchToWhatsappCity(schedule?.partner.facility.IBGECode);

  const tabs = useMemo(() => {
    if (!schedule?.schedulesTypes) {
      return [];
    }

    const hasScheduled = hasType(schedule, ScheduleType.SCHEDULED);
    const hasArrivalOrder = hasType(schedule, ScheduleType.ARRIVAL_ORDER);
    const hasLaboratorial = hasType(schedule, ScheduleType.LABORATORIAL);
    const hasStandaloneTab = hasStandalone(schedule);
    const hasFlex = hasType(schedule, ScheduleType.FLEX);
    const hasScheduledAndArrivalOrder = hasScheduled && hasArrivalOrder;
    const flexTabLabel = isWhatsappCity ? TABS_LABELS[TabsType.WHATSAPP] : TABS_LABELS[TabsType.FLEX];

    const tabsList: TabItem[] = [];

    if (hasScheduled) {
      tabsList.push(TABS_LABELS[TabsType.SCHEDULED]);
    }
    if (hasArrivalOrder) {
      tabsList.push(TABS_LABELS[TabsType.ARRIVAL_ORDER]);
    }
    if (hasLaboratorial) {
      tabsList.push(TABS_LABELS[TabsType.LABORATORIAL]);
    }

    if (hasFlex && hasStandaloneTab && hasScheduledAndArrivalOrder) {
      tabsList.push(flexTabLabel);
      tabsList.push(TABS_LABELS[TabsType.STANDALONE]);

      return tabsList;
    }

    if (hasFlex && !hasStandaloneTab) {
      tabsList.push(flexTabLabel);
    }

    if (!hasFlex && hasStandaloneTab) {
      tabsList.push(TABS_LABELS[TabsType.STANDALONE]);
    }

    if (hasFlex && hasStandaloneTab && !hasScheduledAndArrivalOrder) {
      tabsList.push(flexTabLabel);
      tabsList.push(TABS_LABELS[TabsType.STANDALONE]);
    }

    return tabsList;
  }, [schedule, isWhatsappCity]);

  function onValueChange(value: string) {
    setSelectedTab(value as TabsType);
  }

  return { tabs, selectedTab, onValueChange };
}
